import type { CSSProperties } from "react";

export const FONTS = {
  serif: "Playfair Display",
  sans: "Inter",
};

export const COLORS = {
  gold: "#C5A059",
  goldLight: "#D4B06A",
  goldDark: "#A8883F",
  light: "#F5F4F0",
  lightAlt: "#EFEEEA",
  lightWarm: "#F9F8F4",
  dark: "#1c1917",
  darkAlt: "#292524",
  stone: "#292524",
  stoneLight: "#57534E",
  stoneMuted: "#78716C",
  stoneSubtle: "#A8A29E",
  white: "#FFFFFF",
  black: "#000000",
  textLight: "#F5F4F0",
  textDark: "#1c1917",
  textMuted: "#A09F9C",
  border: "#E7E5E4",
};

export const GLOBS = {
  goldLarge: `radial-gradient(circle, ${COLORS.gold}15 0%, ${COLORS.gold}08 30%, transparent 70%)`,
  goldSmall: `radial-gradient(circle, ${COLORS.gold}10 0%, transparent 60%)`,
  goldGlow: `radial-gradient(circle, ${COLORS.gold}20 0%, ${COLORS.gold}05 40%, transparent 70%)`,
  darkGlow: `radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 60%)`,
  softGlow: `radial-gradient(circle, ${COLORS.gold}08 0%, transparent 50%)`,
};

export const BADGE: CSSProperties = {
  display: "inline-block",
  padding: "4px 14px",
  border: `1px solid ${COLORS.gold}`,
  color: COLORS.gold,
  fontSize: 10,
  letterSpacing: "0.25em",
  textTransform: "uppercase",
  fontWeight: 900,
  borderRadius: 9999,
  backdropFilter: "blur(4px)",
  background: "rgba(255,255,255,0.3)",
};

export const BADGE_DARK: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "5px 14px",
  background: "rgba(41,37,36,0.8)",
  backdropFilter: "blur(8px)",
  color: COLORS.gold,
  fontSize: 10,
  fontWeight: 900,
  letterSpacing: "0.25em",
  textTransform: "uppercase",
  borderRadius: 9999,
  border: `1px solid rgba(197,160,89,0.3)`,
};

export const CTA_ROUND: CSSProperties = {
  display: "inline-block",
  padding: "16px 40px",
  background: COLORS.dark,
  color: COLORS.white,
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  borderRadius: 9999,
  boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
};

export const CTA_GOLD: CSSProperties = {
  display: "inline-block",
  padding: "18px 48px",
  background: COLORS.gold,
  color: COLORS.dark,
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  borderRadius: 9999,
  boxShadow: `0 4px 24px ${COLORS.gold}40`,
};

export const LOGO: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: "50%",
  background: COLORS.gold,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
  fontWeight: 700,
  color: COLORS.white,
  fontFamily: FONTS.serif,
  paddingBottom: 2,
  boxShadow: `0 4px 16px ${COLORS.gold}50`,
};

export const GLASS_LIGHT: CSSProperties = {
  background: "rgba(255,255,255,0.4)",
  backdropFilter: "blur(8px)",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 16,
};

export const GLASS_DARK: CSSProperties = {
  background: "rgba(255,255,255,0.05)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 16,
};

export const RING_DECORATION = (
  size: number,
  thickness: number,
  opacity: number,
  left: string,
  top: string
): CSSProperties => ({
  position: "absolute",
  left,
  top,
  width: size,
  height: size,
  borderRadius: "50%",
  border: `${thickness}px solid ${COLORS.gold}`,
  opacity,
  pointerEvents: "none" as const,
  boxShadow: `0 0 ${size * 0.3}px ${COLORS.gold}20`,
});
